import React from 'react';
import { Hero } from '../components/Hero/Hero';
import { Footer } from '../components/Footer/Footer';
import { MessageSquare, BarChart, Clock } from 'lucide-react';

import { Phone, Zap, Globe, TrendingUp, CheckCircle, ArrowRight, Play, Star, Volume2, Pause } from 'lucide-react';
import { useState } from 'react';

export function Home() {
  const [playingDemo, setPlayingDemo] = useState<number | null>(null);

  const features = [
    {
      icon: Phone,
      title: 'AI Voice Agents',
      description: 'Human-like AI agents that handle calls naturally and professionally'
    },
    {
      icon: Clock,
      title: '24/7 Availability',
      description: 'Never miss a call with round-the-clock automated assistance'
    },
    {
      icon: Globe,
      title: 'Multi-Language',
      description: 'Support customers in 40+ languages with native fluency'
    },
    {
      icon: Zap,
      title: 'Instant Response',
      description: 'Ultra-low latency responses in under 500ms'
    },
    {
      icon: TrendingUp,
      title: 'Sales Automation',
      description: 'Scale outbound campaigns and boost conversion rates'
    },
    {
      icon: CheckCircle,
      title: 'Easy Integration',
      description: 'Connect with your existing tools and workflows seamlessly'
    }
  ];

  const useCases = [
    {
      title: 'Customer Support',
      description: 'Handle inquiries, troubleshoot issues, and provide instant support',
      color: 'bg-blue-50 border-blue-200'
    },
    {
      title: 'Sales & Outreach',
      description: 'Qualify leads, schedule demos, and drive conversions',
      color: 'bg-green-50 border-green-200'
    },
    {
      title: 'Appointment Booking',
      description: 'Manage schedules, send reminders, and reduce no-shows',
      color: 'bg-orange-50 border-orange-200'
    },
    {
      title: 'Survey & Feedback',
      description: 'Collect insights and improve customer satisfaction',
      color: 'bg-cyan-50 border-cyan-200'
    }
  ];

  const stats = [
    { value: '500ms', label: 'Response Time' },
    { value: '40+', label: 'Languages' },
    { value: '99.9%', label: 'Uptime' },
    { value: '24/7', label: 'Availability' }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'CEO at TechFlow Solutions',
      company: 'TechFlow Solutions',
      rating: 5,
      text: 'Aigenie has transformed our customer support. Our response time dropped by 80% and customer satisfaction scores are at an all-time high.',
      avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop'
    },
    {
      name: 'Marcus Chen',
      role: 'VP of Sales at GrowthMetrics',
      company: 'GrowthMetrics',
      rating: 5,
      text: 'We scaled our outbound sales operations 10x without hiring additional staff. The AI agents sound incredibly natural and convert leads consistently.',
      avatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Operations Director at HealthPlus',
      company: 'HealthPlus',
      rating: 5,
      text: 'Appointment scheduling is now completely automated. No-shows decreased by 60% thanks to the intelligent reminder system.',
      avatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop'
    },
    {
      name: 'David Park',
      role: 'Founder at StartupHub',
      company: 'StartupHub',
      rating: 5,
      text: 'As a small startup, Aigenie gave us enterprise-level communication capabilities. Setup took less than an hour and the ROI was immediate.',
      avatar: 'https://images.pexels.com/photos/1516680/pexels-photo-1516680.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop'
    },
    {
      name: 'Jennifer Lee',
      role: 'Customer Success Manager at CloudBase',
      company: 'CloudBase',
      rating: 5,
      text: 'The multilingual support is a game-changer. We now serve customers in 15 countries with the same quality of service.',
      avatar: 'https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop'
    },
    {
      name: 'Michael Thompson',
      role: 'CTO at DataStream',
      company: 'DataStream',
      rating: 5,
      text: 'Integration with our existing tools was seamless. The API is well-documented and the support team is incredibly responsive.',
      avatar: 'https://images.pexels.com/photos/1212984/pexels-photo-1212984.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop'
    }
  ];

  const conversationDemos = [
    {
      title: 'Customer Support Call',
      description: 'AI agent handling a technical support inquiry',
      duration: '2:34',
      category: 'Support'
    },
    {
      title: 'Sales Qualification',
      description: 'Lead qualification and demo scheduling',
      duration: '3:12',
      category: 'Sales'
    },
    {
      title: 'Appointment Reminder',
      description: 'Automated appointment confirmation call',
      duration: '1:45',
      category: 'Scheduling'
    }
  ];

  const integrations = [
    { name: 'Salesforce', logo: 'https://logo.clearbit.com/salesforce.com' },
    { name: 'HubSpot', logo: 'https://logo.clearbit.com/hubspot.com' },
    { name: 'Slack', logo: 'https://logo.clearbit.com/slack.com' },
    { name: 'Zapier', logo: 'https://logo.clearbit.com/zapier.com' },
    { name: 'Twilio', logo: 'https://logo.clearbit.com/twilio.com' },
    { name: 'Google Calendar', logo: 'https://logo.clearbit.com/google.com' },
    { name: 'Microsoft Teams', logo: 'https://logo.clearbit.com/microsoft.com' },
    { name: 'Zendesk', logo: 'https://logo.clearbit.com/zendesk.com' },
    { name: 'Intercom', logo: 'https://logo.clearbit.com/intercom.com' },
    { name: 'Pipedrive', logo: 'https://logo.clearbit.com/pipedrive.com' },
    { name: 'Monday.com', logo: 'https://logo.clearbit.com/monday.com' },
    { name: 'Calendly', logo: 'https://logo.clearbit.com/calendly.com' }
  ];

  return (
    <div className="bg-slate-50">
      <section className="relative overflow-hidden bg-gradient-to-br from-cyan-400 via-blue-500 to-teal-500">
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-400/20 to-sky-400/20"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-white text-sm font-medium mb-8">
                <Zap className="w-4 h-4 mr-2" />
                Next-Generation AI Voice Technology
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                AI-Powered Voice Agents
                <br />
                <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
                  Transform Your Business
                </span>
              </h1>

              <p className="text-xl text-white/90 mb-10 leading-relaxed">
                Automate customer calls, boost sales, and scale operations with intelligent AI voice agents
                that sound natural and deliver results.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button className="group px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-50 transition-all font-medium text-lg shadow-lg shadow-black/20 flex items-center justify-center">
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors font-medium text-lg border border-white/30 flex items-center justify-center">
                  <Play className="mr-2 w-5 h-5" />
                  Watch Demo
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/8867434/pexels-photo-8867434.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                  alt="AI Voice Receptionist"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent"></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mt-16">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Powerful Features for Modern Businesses
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to automate and enhance your communication workflows
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 bg-slate-50 rounded-xl hover:bg-white hover:shadow-xl transition-all duration-300 border border-slate-200"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Deploy AI Voice Agents That Sound Human and Work 24/7
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Listen to real conversations powered by Aigenie
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {conversationDemos.map((demo, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="inline-block px-3 py-1 bg-blue-500/20 text-blue-300 text-xs font-medium rounded-full mb-3">
                      {demo.category}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {demo.title}
                    </h3>
                    <p className="text-sm text-slate-300">
                      {demo.description}
                    </p>
                  </div>
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Audio will play here</span>
                    <span className="text-sm text-slate-400">{demo.duration}</span>
                  </div>
                  <div className="h-1 bg-slate-700 rounded-full">
                    <div className="h-1 bg-blue-500 rounded-full w-0"></div>
                  </div>
                </div>

                <button
                  onClick={() => setPlayingDemo(playingDemo === index ? null : index)}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center"
                >
                  {playingDemo === index ? (
                    <>
                      <Pause className="w-4 h-4 mr-2" />
                      Pause Demo
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 mr-2" />
                      Play Demo
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-400 mb-4">
              Upload your own conversation recordings to showcase custom demos
            </p>
            <button className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors border border-white/20">
              Upload Recording
            </button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Join thousands of businesses transforming their operations with Aigenie
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border-2 border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Connect Your Tools
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Seamless integration with your favorite platforms and tools
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="group bg-slate-50 border-2 border-slate-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden mb-3 bg-white">
                  <img
                    src={integration.logo}
                    alt={integration.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-sm font-medium text-slate-700 text-center">
                  {integration.name}
                </span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
              View All Integrations
            </button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Built for Every Use Case
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From customer support to sales automation, Aigenie adapts to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className={`p-8 rounded-xl border-2 ${useCase.color} hover:shadow-lg transition-all`}
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {useCase.title}
                </h3>
                <p className="text-slate-700 leading-relaxed text-lg">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Join thousands of companies using AI voice agents to scale operations and delight customers
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg shadow-lg">
              Get Started Free
            </button>
            <button className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors font-medium text-lg border border-white/20">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
